/* Swiper's screen-reader messages, in Hebrew.
 *
 * Swiper writes its own aria-labels onto the navigation elements the theme
 * renders, so a label set in Liquid is overwritten the moment a carousel
 * initialises — that is why every arrow on the store announced itself as
 * "Previous slide" to a screen reader on a Hebrew storefront.
 *
 * The theme's carousels are created in minified vendor code that passes no
 * `a11y` options, so rather than patch that file this wraps the constructor and
 * supplies the defaults before each instance is built. An instance that does
 * pass its own `a11y` options still wins.
 *
 * Swiper itself is fetched on demand by js-swiper-boot.js, so the wrapping is
 * exposed as window.jmA11y.wrapSwiper() and the loader calls it the moment the
 * library arrives, before the first carousel is built. When Swiper is already
 * on the page the wrapping is applied here instead, as it always was. Either
 * way every carousel is covered, including ones mounted later by a section
 * re-render in the theme editor.
 */
(function () {
  'use strict';

  var MESSAGES = (window.jmA11y && window.jmA11y.swiper) || null;
  if (!MESSAGES) return;

  function wrapSwiper() {
    if (typeof window.Swiper !== 'function' || window.Swiper.jmA11yWrapped) return;

    var Original = window.Swiper;

    function Swiper(el, params) {
      params = params || {};
      var a11y = params.a11y || {};
      for (var k in MESSAGES) {
        if (Object.prototype.hasOwnProperty.call(MESSAGES, k) && a11y[k] === undefined) {
          a11y[k] = MESSAGES[k];
        }
      }
      params.a11y = a11y;
      return new Original(el, params);
    }

    Swiper.prototype = Original.prototype;
    /* Swiper.use, Swiper.extendDefaults and the module statics are read by the
       bundle itself, so the wrapper has to carry them across. */
    for (var key in Original) {
      if (Object.prototype.hasOwnProperty.call(Original, key)) {
        try { Swiper[key] = Original[key]; } catch (e) { /* read-only static */ }
      }
    }
    Swiper.jmA11yWrapped = true;

    window.Swiper = Swiper;
  }

  window.jmA11y.wrapSwiper = wrapSwiper;
  wrapSwiper();

  /* Slick's custom arrow markup is a DIV across generic theme carousels,
     slideshows and the menu blog carousel. Product media and Quick View have
     component-specific focus/state controllers, so this shared enhancement
     explicitly leaves those two surfaces alone. */
  var slickCarouselSelector = '.slick-slider';
  var slickArrowSelector = slickCarouselSelector + ' > .arrow-prev, ' + slickCarouselSelector + ' > .arrow-next';
  var separatelyManagedSelector = '#content_quickview, .product-single__photos';
  var slickFrame = null;

  function setAttributeIfChanged(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function slickArrowIsManagedHere(arrow) {
    return !!arrow && !arrow.closest(separatelyManagedSelector) &&
      arrow.matches(slickArrowSelector) && arrow.tagName !== 'BUTTON' && arrow.tagName !== 'A';
  }

  function slickArrowIsVisible(arrow) {
    if (!arrow.isConnected || arrow.closest('.slick-cloned, [hidden], [inert], [aria-hidden="true"]')) return false;
    var element = arrow;
    while (element && element !== document.documentElement) {
      var style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      element = element.parentElement;
    }
    return arrow.getClientRects().length > 0;
  }

  function slickArrowIsDisabled(arrow) {
    return arrow.classList.contains('slick-disabled') || arrow.classList.contains('slick-hidden') ||
      !!arrow.closest('.slick-cloned, [hidden], [inert], [aria-hidden="true"]');
  }

  function decorateSlickArrow(arrow) {
    if (!slickArrowIsManagedHere(arrow)) return;
    var isNext = arrow.classList.contains('arrow-next');
    var label = isNext ? MESSAGES.nextSlideMessage : MESSAGES.prevSlideMessage;
    var disabled = slickArrowIsDisabled(arrow);
    setAttributeIfChanged(arrow, 'role', 'button');
    if (label) setAttributeIfChanged(arrow, 'aria-label', label);
    setAttributeIfChanged(arrow, 'aria-disabled', disabled ? 'true' : 'false');
    setAttributeIfChanged(arrow, 'tabindex', label && !disabled && slickArrowIsVisible(arrow) ? '0' : '-1');
  }

  /* Anything hidden from the accessibility tree must not be reachable by
   * keyboard — otherwise focus lands on an element a screen reader will not
   * announce at all, which is axe's aria-hidden-focus and WCAG 4.1.2.
   *
   * The theme already ships that correctly: every decorative card overlay is
   * written as aria-hidden="true" tabindex="-1" in Liquid. Slick then undoes
   * it. Its ADA code walks the active slide and writes tabindex onto every
   * "a, input, button, select" inside it, with no knowledge of aria-hidden.
   *
   * Measured on the live home page: 82 such overlays, of which exactly 6 had
   * lost their tabindex — precisely the ones in slick-current / slick-active
   * slides. That is also why the failure appears only on carousel pages and
   * never on the collection grid, which renders the identical markup.
   *
   * inert would also satisfy the rule, and was rejected: it blocks pointer
   * events too, and the whole purpose of these overlays is to make the card
   * image clickable. Restoring tabindex keeps the click and removes the trap.
   *
   * This needs its own observer rather than riding the arrow sync. A first
   * attempt hung it off synchronizeSlickArrows, whose observer filters on
   * class/style/hidden/aria-*, and it changed nothing: when slick writes only
   * tabindex, nothing in that filter fires, so the sync was never scheduled
   * after the write that caused the problem. The count stayed at exactly 18
   * across the re-scan. The filter here is therefore tabindex itself, plus
   * the two attributes that can newly hide a subtree.
   *
   * writing guards against the obvious consequence: our own setAttribute
   * would re-enter the observer that is watching tabindex.
   */
  var focusableInsideHidden = 'a[href], button, input, select, textarea, [tabindex]';
  var sealFrame = null;
  var writing = false;

  /* האיטום חייב להיות הפיך, וקודם הוא לא היה.
   *
   * syncFilter() ב-js-collection-filters-product.js מסמן את
   * #JmFilterSidebar כ-aria-hidden="true" כל עוד המגירה סגורה, ולכן המעבר
   * כאן הטביע tabindex="-1" על כל מה שבתוכה. כשהמגירה נפתחת, aria-hidden
   * מתהפך ל-"false" — אבל ה-tabindex נשאר. נמדד ב-390x844 עם המגירה
   * פתוחה, בלי inert בשום מקום בשרשרת: .close-filter, .facets__list input,
   * .field__range--min ו-.field__input--min כולם דיווחו tabindex === "-1",
   * והליכת Tab מהמחוון הראשון נתנה
   * ["מחיר מזערי בטווח", BODY, "סגירה (esc)", BODY, ...] — כלומר כל מגירת
   * הסינון בנייד הייתה מתה למקלדת. הסרת התכונות ביד החזירה את ההליכה
   * הנכונה, מה שמבודד את האיטום כגורם היחיד. במחשב זה לא קרה, כי שם
   * המגירה לעולם אינה aria-hidden.
   *
   * לכן כל צומת שאנחנו אוטמים מסומן, ונרשם מה היה לו קודם: ריק פירושו
   * שלא הייתה לו תכונה כלל, 'keep' פירושו שכבר היה לו -1 משלו ואסור לנו
   * לגעת בו (slick כותב כך בעצמו על שקופיות משוכפלות), וכל ערך אחר נשמר
   * כלשונו ומוחזר. המעבר מסיר את האיטום מכל צומת מסומן שכבר אינו בתוך
   * תת-עץ aria-hidden="true" — והמעבר הזה רץ ממילא, כי aria-hidden נמצא
   * ב-attributeFilter של המשקיף.
   */
  var SEAL_FLAG = 'data-jm-sealed';

  function sealOne(node) {
    if (node.hasAttribute(SEAL_FLAG)) return;
    if (node.getAttribute('tabindex') === '-1') {
      node.setAttribute(SEAL_FLAG, 'keep');
      return;
    }
    node.setAttribute(SEAL_FLAG, node.hasAttribute('tabindex') ? node.getAttribute('tabindex') : '');
    node.setAttribute('tabindex', '-1');
  }

  function unsealOne(node) {
    var previous = node.getAttribute(SEAL_FLAG);
    node.removeAttribute(SEAL_FLAG);
    if (previous === 'keep') return;
    if (previous === '') node.removeAttribute('tabindex');
    else node.setAttribute('tabindex', previous);
  }

  function sealAriaHiddenSubtrees() {
    sealFrame = null;
    writing = true;
    try {
      var sealed = [];
      document.querySelectorAll('[aria-hidden="true"]').forEach(function (hidden) {
        if (hidden.matches(focusableInsideHidden)) {
          sealOne(hidden);
          sealed.push(hidden);
        }
        hidden.querySelectorAll(focusableInsideHidden).forEach(function (node) {
          sealOne(node);
          sealed.push(node);
        });
      });
      document.querySelectorAll('[' + SEAL_FLAG + ']').forEach(function (node) {
        if (sealed.indexOf(node) === -1) unsealOne(node);
      });
    } finally {
      writing = false;
    }
  }

  function queueSeal() {
    if (writing || sealFrame !== null) return;
    sealFrame = window.requestAnimationFrame(sealAriaHiddenSubtrees);
  }

  function setupAriaHiddenSeal() {
    if (window.__jmAriaHiddenSealBound || !document.body) return;
    window.__jmAriaHiddenSealBound = true;
    new MutationObserver(queueSeal).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      /* Deliberately not 'class': slick touches slide classes on every frame
         of a transition, and this observer covers the whole body. tabindex is
         the attribute that actually causes the failure, and it is watched
         directly, so class would only add noise. */
      attributeFilter: ['tabindex', 'aria-hidden']
    });
    /* Slick settles over several frames after init and again after each
       slide change, and the last write wins. A single pass on load lands
       before it finishes, so re-assert on the carousel lifecycle too. */
    if (window.jQuery) {
      window.jQuery(document).on(
        'init.jmAriaHiddenSeal reInit.jmAriaHiddenSeal afterChange.jmAriaHiddenSeal setPosition.jmAriaHiddenSeal',
        '.slick-slider',
        queueSeal
      );
    }
    sealAriaHiddenSubtrees();
  }

  function synchronizeSlickArrows() {
    slickFrame = null;
    document.querySelectorAll(slickArrowSelector).forEach(decorateSlickArrow);
  }

  function queueSlickArrowSync() {
    if (slickFrame !== null) return;
    slickFrame = window.requestAnimationFrame(synchronizeSlickArrows);
  }

  function elementTouchesManagedSlick(element) {
    if (!element || element.nodeType !== 1) return false;
    var sliders = [];
    if (element.matches(slickCarouselSelector)) sliders.push(element);
    var parentSlider = element.closest(slickCarouselSelector);
    if (parentSlider) sliders.push(parentSlider);
    element.querySelectorAll(slickCarouselSelector).forEach(function (slider) { sliders.push(slider); });
    return sliders.some(function (slider) { return !slider.closest(separatelyManagedSelector); });
  }

  function slickMutationChanged(mutations) {
    return mutations.some(function (mutation) {
      var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      if (elementTouchesManagedSlick(target)) return true;
      return Array.from(mutation.addedNodes || []).some(function (node) {
        return elementTouchesManagedSlick(node);
      });
    });
  }

  function setupCustomSlickArrows() {
    if (window.__jmCustomSlickA11yBound || !document.body) return;
    window.__jmCustomSlickA11yBound = true;
    /* Retain the original guard for compatibility with cached section markup
       and make clear that the nav-blog handler is part of this single binding. */
    window.__jmNavBlogSlickA11yBound = true;
    document.addEventListener('keydown', function (event) {
      var arrow = event.target.closest && event.target.closest(slickArrowSelector);
      if (!slickArrowIsManagedHere(arrow) || slickArrowIsDisabled(arrow) || !slickArrowIsVisible(arrow)) return;
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      arrow.click();
    });
    ['focusin', 'mouseover'].forEach(function (eventName) {
      document.addEventListener(eventName, function (event) {
        var slider = event.target.closest && event.target.closest(slickCarouselSelector);
        if (slider && !slider.closest(separatelyManagedSelector)) queueSlickArrowSync();
      });
    });
    new MutationObserver(function (mutations) {
      if (slickMutationChanged(mutations)) queueSlickArrowSync();
    }).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-disabled']
    });
    if (window.jQuery) {
      window.jQuery(document).on(
        'init.jmCustomSlickArrows reInit.jmCustomSlickArrows afterChange.jmCustomSlickArrows setPosition.jmCustomSlickArrows',
        slickCarouselSelector,
        function (event) {
          var slider = event && event.currentTarget || this;
          if (slider && slider.closest && !slider.closest(separatelyManagedSelector)) queueSlickArrowSync();
        }
      );
    }
    synchronizeSlickArrows();
  }

  function boot() {
    setupCustomSlickArrows();
    setupAriaHiddenSeal();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
